/**
 * BlogCategoryPage — landing page for /blog/category/<slug>.
 *
 * Resolves the category name + filtered post list. For Phase K we
 * implement client-side filtering on `post.categorySlugs[]` (the
 * marketing-cms join table is denormalised onto the post payload by
 * the resolver). A future MCMS-002 may add a server-side category
 * filter; the API surface here insulates consumers from that change.
 *
 * @module @puffin-core/marketing-shell/blog/BlogCategoryPage
 */

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import BlogPostCard from './BlogPostCard.jsx';
import { DEFAULT_BLOG_UI_STRINGS } from './blogUiStrings.js';

/**
 * useBlogCategoryPosts — resolves the category row + the posts filtered onto it,
 * driving the loading/ready/not-found/error machine. Extracted so
 * <BlogCategoryPage> is left as a pure render of the resolved taxonomy.
 *
 * @returns {{state: string, category: object|null, posts: Array, error: Error|null}}
 */
function useBlogCategoryPosts({ client, categorySlug, locale, initialCategory, initialPosts }) {
  const [state, setState] = useState(initialCategory ? 'ready' : 'loading');
  const [category, setCategory] = useState(initialCategory ?? null);
  const [posts, setPosts] = useState(initialPosts ?? []);
  const [error, setError] = useState(null);
  // BLOG-STD-001: server-rendered category pages arrive seeded — skip the
  // redundant first fetch to avoid a hydration loading-flash.
  const seededRef = useRef(Boolean(initialCategory));

  useEffect(() => {
    if (seededRef.current) {
      seededRef.current = false;
      return undefined;
    }
    let alive = true;
    setState('loading');
    setError(null);
    async function load() {
      // MLC-001 — resolve the taxonomy + posts in the active locale so a
      // Spanish category page shows the Spanish category + its Spanish posts.
      const [cats, allPosts] = await Promise.all([
        client.listBlogCategories({ locale }),
        client.listBlogPosts({ limit: 100, locale }),
      ]);
      if (!alive) return;
      const cat = cats.find((c) => c.slug === categorySlug);
      if (!cat) {
        setState('not_found');
        return;
      }
      const filtered = allPosts.filter(
        (p) => Array.isArray(p.categorySlugs) && p.categorySlugs.includes(categorySlug),
      );
      setCategory(cat);
      setPosts(filtered);
      setState('ready');
    }
    load().catch((err) => {
      if (!alive) return;
      setError(err);
      setState('error');
    });
    return () => {
      alive = false;
    };
  }, [client, categorySlug, locale]);

  return { state, category, posts, error };
}

/**
 * One blog category's page. Props are true to `BlogCategoryPage.propTypes` (PROTECTOR-SMS-001 U1b: the server render
 * through `blogPageElementFor` passes only what it has, so an optional prop must read as optional).
 *
 * @param {Object} props
 * @param {{listBlogCategories: Function, listBlogPosts: Function}} props.client
 * @param {string} props.categorySlug
 * @param {Object} [props.initialCategory]
 * @param {Array} [props.initialPosts]
 * @param {import('react').ReactNode} [props.loadingFallback=null]
 * @param {import('react').ReactNode} [props.notFoundFallback]
 * @param {Function} [props.errorFallback] - `({ error }) => node`
 * @param {string} [props.hrefPrefix='/blog']
 * @param {string} [props.readingMinutesLabel]
 * @param {string} [props.locale='en'] - the content locale
 * @param {Object} [props.strings] - the blog's chrome strings (`getBlogUiStrings`)
 * @returns {import('react').ReactNode} the page, or the fallback its state calls for
 */
export default function BlogCategoryPage({
  client,
  categorySlug,
  initialCategory,
  initialPosts,
  loadingFallback = null,
  notFoundFallback,
  errorFallback,
  hrefPrefix = '/blog',
  readingMinutesLabel,
  // MLC-001: the active CONTENT locale (i18n short tag, e.g. 'en' | 'es'),
  // used for the taxonomy + post fetches AND date display. Defaults to 'en'
  // (the model default) so EN behavior is byte-identical.
  locale = 'en',
  strings,
}) {
  // BLOG-STD-001: chrome strings from the ecosystem SSOT (see blogUiStrings.js);
  // absent `strings` → English defaults so existing callers are byte-identical.
  const s = strings ? { ...DEFAULT_BLOG_UI_STRINGS, ...strings } : DEFAULT_BLOG_UI_STRINGS;
  const resolvedReadingMinutesLabel = readingMinutesLabel ?? s.readingMinutesLabel;
  const { state, category, posts, error } = useBlogCategoryPosts({
    client,
    categorySlug,
    locale,
    initialCategory,
    initialPosts,
  });

  if (state === 'loading') return loadingFallback;
  if (state === 'not_found') return notFoundFallback || null;
  if (state === 'error') {
    if (typeof errorFallback === 'function') return errorFallback({ error });
    return (
      <section className="section-marketing" role="alert" aria-live="assertive">
        <h2 className="section-heading">{s.errorHeading}</h2>
        <p className="section-description">{s.errorBodyCategory}</p>
      </section>
    );
  }

  return (
    <section className="section-marketing post-category">
      <header className="section-heading-group">
        <p className="post-category-eyebrow">{s.categoryEyebrow}</p>
        <h1 className="section-heading">{category.name}</h1>
        {posts.length === 0 ? <p className="section-description">{s.emptyCategory}</p> : null}
      </header>
      {posts.length > 0 ? (
        <ul className="post-grid feature-grid">
          {posts.map((p) => (
            <li key={p.slug}>
              <BlogPostCard
                post={p}
                hrefPrefix={hrefPrefix}
                readingMinutesLabel={resolvedReadingMinutesLabel}
                locale={locale}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

BlogCategoryPage.propTypes = {
  client: PropTypes.shape({
    listBlogCategories: PropTypes.func.isRequired,
    listBlogPosts: PropTypes.func.isRequired,
  }).isRequired,
  categorySlug: PropTypes.string.isRequired,
  initialCategory: PropTypes.object,
  initialPosts: PropTypes.array,
  loadingFallback: PropTypes.node,
  notFoundFallback: PropTypes.node,
  errorFallback: PropTypes.func,
  hrefPrefix: PropTypes.string,
  readingMinutesLabel: PropTypes.string,
  locale: PropTypes.string,
  strings: PropTypes.object,
};
